import React from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  pmLeadService,
  PMLead,
  PMLeadStats,
  PMLeadStatsResponse,
  PMLeadInitiativesResponse
} from '../services/pmLeadService'
import { useJiraConfiguredQuery } from './useJiraConfig'

/**
 * Hook to fetch all PM Leads (only when Jira is configured)
 */
export const usePMLeads = (jql?: string) => {
  return useJiraConfiguredQuery(
    ['pm-leads', jql],
    () => pmLeadService.getAllPMLeads(jql),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      select: (data: any) => {
        if (!data.success) {
          throw new Error(data.error || 'Failed to fetch PM Leads')
        }
        return data.data
      },
      retry: 2,
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    }
  )
}

/**
 * Hook to fetch PM Lead statistics (only when Jira is configured)
 */
export const usePMLeadStats = () => {
  return useJiraConfiguredQuery(
    ['pm-lead-stats'],
    () => pmLeadService.getPMLeadStats(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      select: (data: any) => {
        if (!data.success) {
          throw new Error(data.error || 'Failed to fetch PM Lead statistics')
        }
        return data.data
      },
      retry: 2,
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    }
  )
}

/**
 * Hook to search PM Leads
 */
export const usePMLeadSearch = (searchTerm: string) => {
  return useQuery({
    queryKey: ['pm-leads', 'search', searchTerm],
    queryFn: () => pmLeadService.searchPMLeads(searchTerm),
    enabled: !!searchTerm.trim(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
    select: (data) => {
      if (!data.success) {
        throw new Error(data.error || 'Failed to search PM Leads')
      }
      return data.data
    },
    retry: 1,
  })
}

/**
 * Hook to get initiatives for a specific PM Lead
 */
export const usePMLeadInitiatives = (leadName: string) => {
  return useQuery({
    queryKey: ['pm-lead-initiatives', leadName],
    queryFn: () => pmLeadService.getInitiativesForPMLead(leadName),
    enabled: !!leadName.trim(),
    staleTime: 3 * 60 * 1000, // 3 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
    select: (data) => {
      if (!data.success) {
        throw new Error(data.error || `Failed to fetch initiatives for PM Lead "${leadName}"`)
      }
      return data.data
    },
    retry: 2,
  })
}

/**
 * Hook to get enriched PM Leads with statistics
 */
export const useEnrichedPMLeads = (jql?: string) => {
  const { data: leads, isLoading: leadsLoading, error: leadsError } = usePMLeads(jql)
  const { data: stats, isLoading: statsLoading, error: statsError } = usePMLeadStats()

  const enrichedLeads = React.useMemo(() => {
    if (!leads || !stats) return []

    const leadStatsMap = new Map<string, PMLeadStats>()
    stats.leads.forEach(stat => {
      leadStatsMap.set(stat.name, stat)
    })

    return leads.map(lead => ({
      ...lead,
      count: leadStatsMap.get(lead.name)?.total || 0,
      stats: leadStatsMap.get(lead.name),
      workloadLevel: pmLeadService.getWorkloadLevel(leadStatsMap.get(lead.name)?.total || 0),
      workloadColor: pmLeadService.getWorkloadColor(
        pmLeadService.getWorkloadLevel(leadStatsMap.get(lead.name)?.total || 0)
      ),
      initials: pmLeadService.getPMLeadInitials(lead),
      hasEmail: pmLeadService.hasEmail(lead)
    }))
  }, [leads, stats])

  return {
    data: enrichedLeads,
    isLoading: leadsLoading || statsLoading,
    error: leadsError || statsError,
    leads,
    stats
  }
}

/**
 * Hook to get unique PM Lead names sorted alphabetically
 */
export const usePMLeadNames = (jql?: string) => {
  const { data: leads, isLoading, error } = usePMLeads(jql)

  const leadNames = React.useMemo(() => {
    if (!leads) return []
    return pmLeadService.getUniquePMLeadNames(leads)
  }, [leads])

  return {
    data: leadNames,
    isLoading,
    error
  }
}

/**
 * Hook to get PM Leads grouped by first letter
 */
export const usePMLeadsGrouped = (jql?: string) => {
  const { data: leads, isLoading, error } = usePMLeads(jql)

  const groupedLeads = React.useMemo(() => {
    if (!leads) return {}
    return pmLeadService.groupPMLeadsByLetter(leads)
  }, [leads])

  return {
    data: groupedLeads,
    isLoading,
    error
  }
}

/**
 * Hook to get PM Lead workload distribution
 */
export const usePMLeadWorkloadDistribution = () => {
  const { data: stats, isLoading, error } = usePMLeadStats()

  const workloadDistribution = React.useMemo(() => {
    if (!stats) return { low: 0, medium: 0, high: 0, overloaded: 0 }

    const distribution = { low: 0, medium: 0, high: 0, overloaded: 0 }
    
    stats.leads.forEach(lead => {
      const level = pmLeadService.getWorkloadLevel(lead.total)
      distribution[level]++
    })

    return distribution
  }, [stats])

  return {
    data: workloadDistribution,
    isLoading,
    error,
    stats
  }
}

/**
 * Hook to get top PM Leads by initiative count
 */
export const useTopPMLeads = (limit: number = 5) => {
  const { data: stats, isLoading, error } = usePMLeadStats()

  const topLeads = React.useMemo(() => {
    if (!stats) return []
    
    return stats.leads
      .sort((a, b) => b.total - a.total)
      .slice(0, limit)
      .map(lead => ({
        ...lead,
        workloadLevel: pmLeadService.getWorkloadLevel(lead.total),
        workloadColor: pmLeadService.getWorkloadColor(
          pmLeadService.getWorkloadLevel(lead.total)
        )
      }))
  }, [stats, limit])

  return {
    data: topLeads,
    isLoading,
    error
  }
}

/**
 * Hook for client-side PM Lead filtering
 */
export const useFilteredPMLeads = (searchTerm: string, jql?: string) => {
  const { data: leads, isLoading, error } = usePMLeads(jql)

  const filteredLeads = React.useMemo(() => {
    if (!leads) return []
    return pmLeadService.filterPMLeads(leads, searchTerm)
  }, [leads, searchTerm])

  return {
    data: filteredLeads,
    isLoading,
    error,
    allLeads: leads
  }
}

// Re-export types for convenience
export type { 
  PMLead, 
  PMLeadStats, 
  PMLeadStatsResponse,
  PMLeadInitiativesResponse 
}
